import { I18n } from "@assets/strings";
import { List } from "immutable";
import React, { useCallback, useState } from "react";
import styled from "styled-components";
import { Media } from "@domain/media/media";
import { useIsMobile } from "@utils/hooks";
import { mobile } from "@ui/styles/utils";
import { PrimaryButton } from "@ui/components/buttons/primaryButton";
import { SecondaryButton } from "@ui/components/buttons/secondaryButton";
import { row } from "@ui/components/layout";
import { AssetManager } from "@ui/components/media/assetManager/assetManager";

interface AssetBrowserProps extends React.HTMLAttributes<HTMLDivElement> {
	onCancel?: () => void;
	onAssetsChosen: (assets: List<Media>) => void;
	allowedTypes?: string[];
	small?: boolean;
	multiple?: boolean;
}

export const AssetBrowser: React.FC<AssetBrowserProps> = ({
	onCancel,
	onAssetsChosen,
	allowedTypes,
	small,
	multiple,
	...htmlProps
}) => {
	const [selectedAssets, setSelectedAssets] = useState(List<Media>());
	const isMobile = useIsMobile();

	const assetDisabler = useCallback(
		(asset: Media) => !!allowedTypes && !allowedTypes.find((type) => asset.mimeType.match(type)),
		[allowedTypes]
	);

	return (
		<Container {...htmlProps}>
			<StyledAssetManager
				selectedAssets={selectedAssets}
				onAssetsSelected={(a) => setSelectedAssets(a)}
				assetDisabler={assetDisabler}
				small={small}
				multiSelectEnabled={multiple}
			/>
			<Actions>
				{onCancel && (
					<SecondaryButton light={isMobile} onClick={onCancel}>
						{I18n.t("back")}
					</SecondaryButton>
				)}
				<PrimaryButton
					light={isMobile}
					data-asset="true"
					disabled={selectedAssets.size < 1}
					onClick={() => selectedAssets.size > 0 && onAssetsChosen(selectedAssets)}
				>
					{I18n.t(isMobile ? "add" : "asset_manager.asset.add")}
				</PrimaryButton>
			</Actions>
		</Container>
	);
};

const Container = styled.div`
	position: relative;
`;

const Actions = styled.div`
	${row(25)};
	padding: 28px 0 0;

	@media ${mobile} {
		padding: 12px 0 0;
		${row(6, "space-around")};
		> button {
			padding: 8px 30px;
		}
	}
`;

const StyledAssetManager = styled(AssetManager)<{ small?: boolean }>`
	height: 500px;
	@media ${mobile} {
		max-height: ${({ small }) => `calc(90vh - ${small ? 180 : 110}px)`};
	}
`;
